import React from 'react';
import './ProviderShell.css';

/**
 * Per-provider error boundary for the ProviderShell iframe system.
 *
 * Catches render errors originating from a specific provider's iframe wrapper
 * and displays a branded error state with Retry and Contact Support actions.
 *
 * Props:
 *   providerId    {string}  - The provider identifier (for logging)
 *   providerName  {string}  - Human-readable provider name (shown in error UI)
 *   onRetry       {func}    - Optional callback invoked when the user clicks Retry.
 *                             The boundary also resets its own internal state.
 *   children      {node}    - The wrapped content (iframe wrapper)
 */
export class ProviderErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // eslint-disable-next-line no-console
        console.error(
            `[ProviderShell] Provider "${this.props.providerId}" error:`,
            error,
            errorInfo
        );
    }

    handleRetry = () => {
        // Reset internal state so children re-render
        this.setState({ hasError: false, error: null });
        // Notify parent so it can destroy + re-init the iframe
        if (typeof this.props.onRetry === 'function') {
            this.props.onRetry();
        }
    };

    handleContactSupport = () => {
        window.location.href = 'mailto:support@nurturecrm.ai';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="provider-error">
                    <div className="provider-error-icon">
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>
                    <h3 className="provider-error-title">
                        Unable to load {this.props.providerName || 'this module'}
                    </h3>
                    <p className="provider-error-message">
                        {this.state.error?.message ||
                            'An unexpected error occurred while loading this module.'}
                    </p>
                    <div className="provider-error-actions">
                        <button
                            className="provider-error-btn provider-error-btn-primary"
                            onClick={this.handleRetry}
                        >
                            Retry
                        </button>
                        <button
                            className="provider-error-btn provider-error-btn-secondary"
                            onClick={this.handleContactSupport}
                        >
                            Contact Support
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ProviderErrorBoundary;
